import { isNil } from 'lodash'
import { useFetchHagerBridgeInstallation } from 'src/Libs/e-mobility/shared/api/use-fetch-hager-bridge-installation'
import { HagerBridgeErrorResponse } from 'src/Libs/e-mobility/shared'

export enum EMobilityImplementation {
  V1,
  V2,
}

export enum EMobilityImplementationCheckStatus {
  Error = 'Error',
  Warning = 'Warning',
  Success = 'Success',
}

interface UseSelectEMobilityImplementationHook {
  eMobilityImplementation: EMobilityImplementation | undefined
  isLoading: boolean
  status: EMobilityImplementationCheckStatus
}

type UseSelectEMobilityImplementationProps = {
  installationId: number | undefined
  skipCheck: boolean
  isRootCheck?: boolean
}

/**
 * The current E-mobility features in production use E3/DC backend, which has several limitations: prices can change
 * after a charging session ends, poor mobile responsiveness, and performance issues.
 *
 * The P3 team is now reimplementing these features using P3's backend service, HagerBridge. So far, we’ve been
 * working on private branches (release/dev-em2d, release/dev-em2t, release/dev-em2a, release/dev-emv2), each targeting
 * a different HagerBridge instance (dev, test, acceptance, staging).
 *
 * Initially, there was no requirement to support both the old and new frontends in the same codebase. To keep things
 * clean, we removed the old code step by step. As a result, the staging branch contains only the new implementation.
 * However, there's a request to roll out the new frontend gradually, installation by installation. Whether a user
 * sees the new or old version depends on the migration status, which can be checked via
 * GET HAGER_BRIDGE_URL/installations/:installationId. If the "newEmobilityEnabled" flag is true, the new UI is shown.
 *
 * This hook decides if user should be shown either:
 *
 * - the old e-mobility section(using old e-mobility APIs provided by E3/DC)
 * - new e-mobility section(using e-mobility APIs provided by P3 via HagerBridge)
 */
export const useSelectEMobilityImplementation = ({
  installationId,
  skipCheck,
  isRootCheck,
}: UseSelectEMobilityImplementationProps): UseSelectEMobilityImplementationHook => {
  const { installation, isLoading, isError, error } = useFetchHagerBridgeInstallation({
    installationId,
    isInstallationDeleted: skipCheck,
    /**
     * We assign a separate query key for the root check to prevent an infinite loop of network requests.
     *
     * React Query identifies queries by their `queryKey`. When multiple components use the same key,
     * they share the same query state (`data`, `isLoading`, `isError`). If the network request fails
     * immediately (e.g., because the URL is invalid or the backend is unreachable), each component
     * mounting or re-rendering triggers a refetch. This can result in an endless loop of requests.
     *
     * By giving the root check its own query key:
     * 1. Its query state is independent of other subscribers of the hook.
     * 2. Failing requests here will not trigger refetches in other components.
     * 3. We avoid repeated network requests caused by multiple subscribers reacting to the same failed query.
     */
    queryKey: isRootCheck ? 'hagerBridgeInstallationRoot' : undefined,
  })

  const doesNotExistInHagerBridge = !isLoading && error?.response?.data?.errorCode === HagerBridgeErrorResponse.INSTALLATION_NOT_FOUND

  /**
   * Specific use case: for installations that exist in the installation service, but haven't reached HagerBridge. This occurs
   * either when:
   * - installation creation event coming from the installation service is not yet processed by HagerBridge(brief time window)
   * - a wallbox was never connected to the installation, so no events related to e-mobility functionality reached HagerBridge,
   * therefore the installation should not be recreated in HagerBridge and will return a 404 when attempting to fetch installation
   * details from HagerBridge
   *
   * In this case, we ignore the 404 error from the network response and override the isError flag with a sort of warning.
   */
  if (doesNotExistInHagerBridge) {
    return {
      isLoading,
      status: EMobilityImplementationCheckStatus.Success,
      eMobilityImplementation: EMobilityImplementation.V1,
    }
  }

  /**
   * Installation must have a valid ID provided and fetching its details must complete without errors, otherwise we assume
   * there is a critical bug in frontend OR HagerBridge is down, which makes the new e-mobility unusable and we must default to
   * the old implementation.
   */
  if (isNil(installationId) || isError) {
    return {
      isLoading,
      status: EMobilityImplementationCheckStatus.Warning,
      eMobilityImplementation: EMobilityImplementation.V1,
    }
  }

  return {
    eMobilityImplementation: installation?.newEmobilityEnabled ? EMobilityImplementation.V2 : EMobilityImplementation.V1,
    isLoading,
    status: isError ? EMobilityImplementationCheckStatus.Error : EMobilityImplementationCheckStatus.Success,
  }
}
